import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Input } from '@heroui/input';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import { useAuth } from '../../contexts/AuthContext';

const Security: React.FC = () => {
  const pageTitle = 'Account Security';
  const pageDescription = 'Manage passwords and connected sign-in providers.';
  const { user, changePassword, resendConfirmationEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      toast.success('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthColor = (strength: number) => {
    if (strength < 2) return 'text-danger-500';
    if (strength < 4) return 'text-warning-500';
    return 'text-success-500';
  };

  const getPasswordStrengthText = (strength: number) => {
    if (strength < 2) return 'Weak';
    if (strength < 4) return 'Medium';
    return 'Strong';
  };

  const isPasswordFormValid = () => {
    return (
      passwordForm.currentPassword &&
      passwordForm.newPassword &&
      passwordForm.confirmPassword &&
      passwordForm.newPassword === passwordForm.confirmPassword &&
      passwordForm.newPassword.length >= 6
    );
  };

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='space-y-6'>
        {/* Change Password (hidden for SSO users) */}
        {(!user?.oauthProviders || user.oauthProviders.length === 0) && (
          <Card>
            <CardHeader>
              <div className='flex items-center space-x-3'>
                <KeyIcon className='w-6 h-6 text-primary-600' />
                <div>
                  <h2 className='text-xl font-semibold'>Change Password</h2>
                  <p className='text-gray-600'>Update your account password</p>
                </div>
              </div>
            </CardHeader>
            <CardBody className='space-y-4'>
              <Input
                type='password'
                label='Current Password'
                placeholder='Enter your current password'
                value={passwordForm.currentPassword}
                onChange={e =>
                  handlePasswordChange('currentPassword', e.target.value)
                }
              />

              <Input
                type='password'
                label='New Password'
                placeholder='Enter new password'
                value={passwordForm.newPassword}
                onChange={e =>
                  handlePasswordChange('newPassword', e.target.value)
                }
                description={
                  passwordForm.newPassword && (
                    <span
                      className={getPasswordStrengthColor(
                        getPasswordStrength(passwordForm.newPassword)
                      )}
                    >
                      Password strength:{' '}
                      {getPasswordStrengthText(
                        getPasswordStrength(passwordForm.newPassword)
                      )}
                    </span>
                  )
                }
              />

              <Input
                type='password'
                label='Confirm New Password'
                placeholder='Confirm new password'
                value={passwordForm.confirmPassword}
                onChange={e =>
                  handlePasswordChange('confirmPassword', e.target.value)
                }
                isInvalid={
                  !!(
                    passwordForm.confirmPassword &&
                    passwordForm.newPassword !== passwordForm.confirmPassword
                  )
                }
                errorMessage={
                  passwordForm.confirmPassword &&
                  passwordForm.newPassword !== passwordForm.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
              />

              <div className='flex justify-end'>
                <Button
                  color='primary'
                  onClick={handleChangePassword}
                  isLoading={isLoading}
                  isDisabled={!isPasswordFormValid()}
                >
                  Change Password
                </Button>
              </div>

              <div className='mt-4 p-4 bg-gray-50 rounded-lg'>
                <h4 className='font-medium text-gray-900 mb-2'>
                  Password Requirements:
                </h4>
                <ul className='text-sm text-gray-600 space-y-1'>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        passwordForm.newPassword.length >= 8
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>At least 8 characters long</span>
                  </li>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        /[A-Z]/.test(passwordForm.newPassword)
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>Contains uppercase letter</span>
                  </li>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        /[a-z]/.test(passwordForm.newPassword)
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>Contains lowercase letter</span>
                  </li>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        /[0-9]/.test(passwordForm.newPassword)
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>Contains number</span>
                  </li>
                  <li className='flex items-center space-x-2'>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        /[^A-Za-z0-9]/.test(passwordForm.newPassword)
                          ? 'bg-success-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span>Contains special character</span>
                  </li>
                </ul>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Connected Accounts - removed */}
        {/* Social sign-in section removed */}

        {/* Account Security Status */}
        <Card>
          <CardHeader>
            <div className='flex items-center space-x-3'>
              <ShieldCheckIcon className='w-6 h-6 text-primary-600' />
              <div>
                <h2 className='text-xl font-semibold'>Account Security</h2>
                <p className='text-gray-600'>Your account security status</p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className='space-y-4'>
              <div className='flex items-center justify-between p-4 border rounded-lg'>
                <div className='flex items-center space-x-3'>
                  {user?.emailConfirmed ? (
                    <CheckCircleIcon className='w-6 h-6 text-success-500' />
                  ) : (
                    <ExclamationTriangleIcon className='w-6 h-6 text-warning-500' />
                  )}
                  <div>
                    <h3 className='font-medium'>Email Verification</h3>
                    <p className='text-sm text-gray-600'>
                      {user?.emailConfirmed
                        ? 'Your email address is verified'
                        : 'Your email address needs verification'}
                    </p>
                  </div>
                </div>
                {!user?.emailConfirmed &&
                (!user?.oauthProviders || user.oauthProviders.length === 0) ? (
                  <Button
                    size='sm'
                    variant='bordered'
                    onClick={async () => {
                      try {
                        await resendConfirmationEmail();
                        toast.success('Verification email sent');
                      } catch (e: any) {
                        toast.error(
                          e?.response?.data?.message ||
                            'Failed to send verification email'
                        );
                      }
                    }}
                  >
                    Resend Email
                  </Button>
                ) : (
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      user?.emailConfirmed
                        ? 'bg-success-100 text-success-700'
                        : 'bg-warning-100 text-warning-700'
                    }`}
                  >
                    {user?.emailConfirmed ? 'Verified' : 'Unverified'}
                  </div>
                )}
              </div>

              {/* Removed Social Sign-In and Password status sections */}
            </div>
          </CardBody>
        </Card>

        {/* Security Tips */}
        <Card>
          <CardHeader>
            <div>
              <h2 className='text-xl font-semibold'>Security Tips</h2>
              <p className='text-gray-600'>Keep your account secure</p>
            </div>
          </CardHeader>
          <CardBody>
            <div className='space-y-4'>
              <div className='flex space-x-3'>
                <ShieldCheckIcon className='w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0' />
                <div>
                  <h4 className='font-medium'>Use a strong password</h4>
                  <p className='text-sm text-gray-600'>
                    Choose a password that's at least 8 characters long and
                    includes a mix of letters, numbers, and symbols.
                  </p>
                </div>
              </div>

              <div className='flex space-x-3'>
                <CheckCircleIcon className='w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0' />
                <div>
                  <h4 className='font-medium'>Verify your email</h4>
                  <p className='text-sm text-gray-600'>
                    A verified email address helps us keep your account secure
                    and allows password recovery.
                  </p>
                </div>
              </div>

              <div className='flex space-x-3'>
                <ExclamationTriangleIcon className='w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0' />
                <div>
                  <h4 className='font-medium'>
                    Be cautious with public computers
                  </h4>
                  <p className='text-sm text-gray-600'>
                    Always log out completely when using shared or public
                    computers.
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default Security;
